import {
  SEARCH_PARAMETER_NAMES,
  type SearchParameterName,
  type SearchParameters,
} from 'app/domain';

/** One parameter the two resolutions disagree about. */
export type ResolutionDrift = {
  parameter: SearchParameterName;
  /** What this app's rules resolved. */
  resolved: string | undefined;
  /** What the page actually searched under. */
  searched: string | undefined;
};

/** A list here, an object for agreements, a joined token on the payload's side. */
const partsOf = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).map(String);
  }

  return String(value).split(',');
};

/**
 * One value as the tokens it names, or none.
 *
 * The two sides spell the same answer several ways. "Nothing" is absent,
 * `null` or the empty string. A multi-valued filter is a list here and a
 * joined token on the payload's side. Agreements are an object here —
 * `{ premiumaccess: 'pa:123' }` — where stringifying would give
 * `[object Object]`, which matches the payload either never (noise on every
 * agreement search) or always (hiding every disagreement in the agreement
 * rules).
 */
const tokensOf = (value: unknown): string[] => {
  if (value === undefined || value === null || value === '') return [];

  const parts = partsOf(value);

  return parts.map((part) => part.trim()).filter(Boolean);
};

/**
 * Compared by. Neither side promises an order and none is meaningful in a
 * multi-valued filter, so `square,vertical` does not read as a disagreement
 * with `vertical,square`.
 */
const asTokens = (value: unknown): string | undefined => {
  const tokens = tokensOf(value);

  return tokens.length ? [...tokens].sort().join(',') : undefined;
};

/** Reported as: what the side actually held, in the order it held it. */
const asText = (value: unknown): string | undefined => {
  const tokens = tokensOf(value);

  return tokens.length ? tokens.join(',') : undefined;
};

/**
 * Where this app's entry resolution disagrees with the search the page ran.
 *
 * The server resolves the same question — `Query.create` runs the url, the
 * stored preferences and the defaults through `SearchParams#build` — and
 * serializes its answer into the rendered payload. So every real request is
 * already a worked example, with a real url, a real cookie and a real locale:
 * better evidence than any fixture, and the only evidence that covers the
 * localized gateway paths at all.
 *
 * Compared on the *effective* values rather than the serialized query, because
 * the two serialize for different reasons — these rules drop a default so one
 * search yields one url, and the payload keeps it. `get` answers what the
 * search runs under on both sides, which is the thing that has to match.
 *
 * A parameter neither side asked for is not compared: this app answers an
 * unstated parameter with its schema default and the payload simply omits it,
 * so comparing the two would report every defaulting parameter on every
 * request and say nothing. A value either side *states* is compared against
 * the other's effective answer, default included — which is where a default
 * that disagrees with the server would show up.
 *
 * Nothing is exempt. The sort was, while a market rule the server applied had
 * deliberately not been ported — that rule has since been removed from the
 * server too, so the two agree and the exemption would only hide drift.
 *
 * One known difference will show up here and is worth seeing rather than
 * suppressing: the server recognises `purerecency` for creative-image searches
 * and `SortEnum` has no such member, so a url stating it reads as unrecognised
 * on this side and resolves to a default instead.
 *
 * Read-only: this reports, and nothing resolves differently because of it.
 */
export const searchResolutionDrift = (
  resolved: SearchParameters,
  searched: Readonly<Record<string, unknown>> | undefined
): ResolutionDrift[] => {
  if (!searched) return [];

  return SEARCH_PARAMETER_NAMES.flatMap((parameter) => {
    const theirs = searched[parameter];

    // Neither side asked for it. A parameter this app defaults and the
    // payload omits is not a disagreement — both searches are silent about it,
    // and comparing a default against an absence would report every defaulting
    // parameter on every request.
    if (!resolved.states(parameter) && asTokens(theirs) === undefined) {
      return [];
    }

    const ours = resolved.get(parameter);

    // Compared on the sorted tokens, reported as each side held it — a sorted
    // value in the report would name something neither side actually has.
    return asTokens(ours) === asTokens(theirs)
      ? []
      : [{ parameter, resolved: asText(ours), searched: asText(theirs) }];
  });
};
